import React, {
  useState,
  useRef,
  useMemo,
  memo,
  useEffect,
  useContext,
  useCallback
} from 'react';
import classNames from 'classnames';
import { Document } from 'react-pdf';
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentRef
} from 'overlayscrollbars-react';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { DocumentMetadataDto } from '@api/Api';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  PDF_PAGE_BASE_SIZE,
  getInitialPdfPageScale,
  getPdfPageSizes
} from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import PdfPage from '@pages/MediaViewer/AssetViewer/PdfCard/components/PdfPage';
import type { PdfCardProps } from '@pages/MediaViewer/AssetViewer/PdfCard/PdfCard';
import { usePdfFabricCanvas } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfFabricCanvas';
import { usePdfScroll } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfScroll';
import {
  PdfPinchGestureState,
  useMobilePdfPinchZoom
} from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/useMobilePdfPinchZoom';
import { useMobilePdfZoomOption } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/useMobilePdfZoomOption';
import { useMobilePdfTextHighlight } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/useMobilePdfTextHighlight';
import {
  CheckIcon,
  CrossIcon,
  ShapesDrawingToolsTextHighlightIcon
} from '@icons';
import {
  isAnnotationDrawTool,
  isCommentFormOpen,
  CURSOR_DRAW_TOOL
} from '@pages/MediaViewer/helpers/drawToolShape';

const maxZoom = 3;

export default memo(function PdfCardMobile({
  onReady,
  asset,
  isAssetReady
}: PdfCardProps) {
  const fabricContext = useContext(FabricContext);
  const {
    selectedCommentId,
    shapesDrawingTools,
    canvasMode,
    activePageNumber,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    documentHighlight,
    selectComment,
    setActivePageNumber,
    setDocumentHighlight,
    setZoomFactor,
    setZoomOption,
    setCanvasMode,
    isMobileDrawingToolbarOpen,
    setDrawToolsShape,
    setIsCommentFieldFocused,
    setActivePageHasText
  } = useAssetViewerState();
  const isShapeDrawing =
    shapesDrawingTools.shape !== CURSOR_DRAW_TOOL && isMobileDrawingToolbarOpen;
  const isTextHighlightMode = shapesDrawingTools.shape === 'documentHighlight';
  const isMainCanvasVisible =
    !!selectedCommentId ||
    (isAnnotationDrawTool(shapesDrawingTools.shape) &&
      canvasMode === 'edit' &&
      isMobileDrawingToolbarOpen);
  // Viewing a comment (not editing it): the main canvas only shows it and
  // lets other pins be clicked. The cursor is the tool in use meanwhile (see
  // AssetViewerStateContext), so nothing is drawn there.
  const isViewingComment =
    !!selectedCommentId &&
    !isCommentFormOpen(selectedCommentId) &&
    canvasMode === 'view';

  const wrapperRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const pageCanvasRef = useRef<HTMLCanvasElement>(null);
  const pageRefsMap = useRef(new Map<number, HTMLDivElement>());
  const pdfPinchGestureRef = useRef<PdfPinchGestureState>({
    isActive: false,
    startSpan: 0,
    baseZoom: 1,
    anchorPage: 0,
    anchorRatioX: 0,
    anchorRatioY: 0,
    anchorViewportOffsetX: 0,
    anchorViewportOffsetY: 0,
    anchorContentX: 0,
    anchorContentY: 0
  });
  const fitZoomRef = useRef(zoomFactor);
  const skipActivePageScrollSyncRef = useRef(false);
  const isLoadPdf = useRef<boolean>(false);
  const mobilePagesReadyRef = useRef(new Set<number>());
  const pageHasTextRef = useRef(new Map<number, boolean>());

  const fileUrl = useMemo(() => {
    const { proxy } = asset.metadata as DocumentMetadataDto;
    return proxy.url || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  const [pagesCount, setPagesCount] = useState(1);
  const [pageRefsVersion, setPageRefsVersion] = useState(0);
  const [mobilePagesReadyVersion, setMobilePagesReadyVersion] = useState(0);
  const [pdfScrollViewport, setPdfScrollViewport] = useState<Element | null>(
    null
  );
  const [isPageSizeReady, setIsPageSizeReady] = useState(false);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({
    width: 1605,
    height: 2272
  });
  // Annotation coordinates are shared with desktop, which sizes pages from a
  // larger base: annotations are scaled by mobile base / desktop base
  const [annotationScale, setAnnotationScale] = useState(1);
  const annotationZoom = zoomFactor * annotationScale;
  // Every page's size at scale 1 (rotation applied), read from the document
  const [pageNaturalSizes, setPageNaturalSizes] = useState<
    { width: number; height: number }[]
  >([]);

  useEffect(() => {
    isLoadPdf.current = false;
    mobilePagesReadyRef.current.clear();
    pageHasTextRef.current.clear();
    setActivePageHasText(true);
    setIsPageSizeReady(false);
  }, [asset.id, setActivePageHasText]);

  const _pageSize = useMemo(() => {
    return {
      width: pageSize.width * zoomFactor,
      height: pageSize.height * zoomFactor
    };
  }, [pageSize, zoomFactor]);

  const pagesArray = useMemo(
    () =>
      new Array(pagesCount).fill('').map((_, index) => ({ page: index + 1 })),
    [pagesCount]
  );

  // Pages share the same width on mobile, each with its own proportions
  const pageHeights = useMemo(
    () =>
      pagesArray.map(({ page }) => {
        const naturalSize = pageNaturalSizes[page - 1];
        return naturalSize
          ? (_pageSize.width * naturalSize.height) / naturalSize.width
          : _pageSize.height;
      }),
    [pagesArray, pageNaturalSizes, _pageSize]
  );

  const onRegisterMobilePageRoot = useCallback(
    (page: number, element: HTMLDivElement | null) => {
      const hadElement = pageRefsMap.current.has(page);
      if (element) {
        pageRefsMap.current.set(page, element);
        if (!hadElement) setPageRefsVersion((value) => value + 1);
        return;
      }
      pageRefsMap.current.delete(page);
      if (hadElement) setPageRefsVersion((value) => value + 1);
    },
    []
  );

  const onMobilePageLoadSuccess = useCallback((page: number) => {
    if (mobilePagesReadyRef.current.has(page)) return;
    mobilePagesReadyRef.current.add(page);
    setMobilePagesReadyVersion((value) => value + 1);
  }, []);

  const onMobilePageTextContent = useCallback(
    (page: number, hasText: boolean) => {
      pageHasTextRef.current.set(page, hasText);
      if (page === activePageNumber) setActivePageHasText(hasText);
    },
    [activePageNumber, setActivePageHasText]
  );

  useEffect(() => {
    setActivePageHasText(
      pageHasTextRef.current.get(activePageNumber) !== false
    );
  }, [activePageNumber, setActivePageHasText]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const viewport =
        wrapperRef.current?.osInstance()?.elements()?.viewport ?? null;
      setPdfScrollViewport(viewport);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isAssetReady, pagesCount]);

  const onLoadSuccess = useCallback(
    (pdfPage: { originalWidth: number; originalHeight: number }) => {
      if (isLoadPdf.current) return;
      isLoadPdf.current = true;
      const { originalWidth, originalHeight } = pdfPage;
      const mobilePageSize = getInitialPdfPageScale(
        originalWidth,
        originalHeight,
        PDF_PAGE_BASE_SIZE,
        maxZoom,
        true
      );
      const desktopPageSize = getInitialPdfPageScale(
        originalWidth,
        originalHeight,
        PDF_PAGE_BASE_SIZE,
        maxZoom,
        false
      );
      setPageSize(mobilePageSize);
      setAnnotationScale(mobilePageSize.width / desktopPageSize.width);
      setIsPageSizeReady(true);
    },
    []
  );

  const { runCanvasLayout, onClearHighlightObjects, renderTextHighlights } =
    usePdfFabricCanvas({
      fabricContext,
      wrapperRef,
      contentRef,
      canvasContainerRef,
      pageRefsMap,
      activePageNumber,
      zoomFactor: annotationZoom,
      turnoverDegree,
      isPageSizeReady,
      selectedCommentId,
      pageRefsVersion,
      pagesReadyVersion: mobilePagesReadyVersion,
      isMainCanvasVisible,
      isMainCanvasPassThrough: isViewingComment,
      selectComment,
      setCanvasMode,
      setDocumentHighlight
    });

  const {
    renderRange,
    visiblePageNumber,
    updateActivePageFromScroll,
    setActivePageInView
  } = usePdfScroll({
    wrapperRef,
    pageRefsMap,
    skipActivePageScrollSyncRef,
    pagesArray,
    pagesCount,
    pageHeights,
    isPageSizeReady,
    pageRefsVersion,
    activePageNumber,
    selectedCommentId,
    shapesDrawingTools,
    runCanvasLayout,
    setActivePageNumber,
    selectComment,
    setDocumentHighlight,
    setCanvasMode
  });

  useMobilePdfPinchZoom({
    pdfScrollViewport,
    wrapperRef,
    contentRef,
    pageRefsMap,
    pdfPinchGestureRef,
    fitZoomRef,
    skipActivePageScrollSyncRef,
    zoomFactor,
    setZoomFactor,
    updateActivePageFromScroll
  });

  const { isFitZoomApplied } = useMobilePdfZoomOption({
    wrapperRef,
    pdfPinchGestureRef,
    fitZoomRef,
    pageSize,
    turnoverDegree,
    isPageSizeReady,
    zoomOption,
    setZoomFactor,
    setZoomOption
  });

  const { selectedTextLength, onCancelTextHighlight, onConfirmTextHighlight } =
    useMobilePdfTextHighlight({
      pageRefsMap,
      pageCanvasRef,
      pdfPinchGestureRef,
      isShapeDrawing,
      shapesDrawingTools,
      selectedCommentId,
      activePageNumber,
      documentHighlight,
      zoomFactor,
      onClearHighlightObjects,
      renderTextHighlights,
      setDocumentHighlight,
      setDrawToolsShape,
      setIsCommentFieldFocused
    });

  return (
    <Document
      className={classNames('media_viewer_pdf', {
        'b-hide-shapes-drawing':
          shapesDrawingTools?.shape === 'documentHighlight' && isShapeDrawing,
        'b-shapes-drawing': isShapeDrawing
      })}
      file={fileUrl}
      onLoadSuccess={(pdf) => {
        setPagesCount(pdf.numPages);
        onReady();
        getPdfPageSizes(pdf).then((sizes) => {
          if (!sizes.length) return;
          setPageNaturalSizes(sizes);
          // Sizes the pages from the document, before any page is rendered
          onLoadSuccess({
            originalWidth: sizes[0].width,
            originalHeight: sizes[0].height
          });
        });
      }}
      loading={<PlaybackPreparingLoader delayed />}
    >
      <div className="media_viewer_pdf-page-indicator">
        {visiblePageNumber} of {pagesCount}
      </div>
      <OverlayScrollbarsComponent
        className="media_viewer_pdf-wrapper"
        ref={wrapperRef}
      >
        <div
          className={classNames(
            'media_viewer_pdf-content',
            'media_viewer_pdf-content-mobile'
          )}
          ref={contentRef}
        >
          <div
            className="media_viewer_pdf-mobile-pages"
            onContextMenu={
              isTextHighlightMode ? (e) => e.preventDefault() : undefined
            }
          >
            {/* Rendered once sized, not at a default size first */}
            {isPageSizeReady &&
              isFitZoomApplied &&
              pagesArray.map(({ page }) => (
                <PdfPage
                  key={page}
                  pageNumber={page}
                  pageWidth={_pageSize.width}
                  pageHeight={pageHeights[page - 1]}
                  zoomFactor={annotationZoom}
                  turnoverDegree={turnoverDegree}
                  isActive={page === activePageNumber}
                  isMainCanvasVisible={isMainCanvasVisible}
                  isViewingComment={isViewingComment}
                  isFirstPage={page === 1}
                  shouldRender={
                    page >= renderRange.start && page <= renderRange.end
                  }
                  scrollViewport={pdfScrollViewport}
                  pageCanvasRef={pageCanvasRef}
                  onRegisterPageRoot={onRegisterMobilePageRoot}
                  onFirstPageLoadSuccess={onLoadSuccess}
                  onPageLoadSuccess={onMobilePageLoadSuccess}
                  onPageTextContent={onMobilePageTextContent}
                  // No hover on touch: a tap makes the page active
                  onActivatePage={setActivePageInView}
                />
              ))}
          </div>
          <div ref={canvasContainerRef} className="media_viewer_pdf-canvas">
            <FabricCanvas />
          </div>
        </div>
      </OverlayScrollbarsComponent>
      {(!isAssetReady || !isPageSizeReady || !isFitZoomApplied) && (
        <PlaybackPreparingLoader delayed />
      )}
      {isTextHighlightMode && (
        <div className="media_viewer_pdf-text-highlight-mobile">
          <div className="media_viewer_pdf-text-highlight-mobile-icon">
            <ShapesDrawingToolsTextHighlightIcon />
          </div>
          <div className="media_viewer_pdf-text-highlight-mobile-content">
            {selectedTextLength
              ? `${selectedTextLength} characters selected`
              : 'Select text to attach'}
          </div>
          <div className="media_viewer_pdf-text-highlight-mobile-btn-container">
            <div
              className="media_viewer_pdf-text-highlight-mobile-btn"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCancelTextHighlight();
              }}
            >
              <CrossIcon />
            </div>
            {!!selectedTextLength && (
              <div
                className="media_viewer_pdf-text-highlight-mobile-btn"
                onPointerDown={onConfirmTextHighlight}
              >
                <CheckIcon />
              </div>
            )}
          </div>
        </div>
      )}
    </Document>
  );
});
